import { formatReqVolume, formatAboBV } from "../../../../components/Lit/acc-mixins/l10n.js";
import { useTranslation, Trans } from "react-i18next";
import { ReactNode, RefObject } from "react";
import { storedLocale } from "../../../../components/Authentication/util";
import { useAppSelector } from "../../../../store";
import LeafProgressCircle from "../../../leaf/LeafProgressCircle/LeafProgressCircle";
import LeafTag from "../../../leaf/LeafTag/LeafTag";
import LeafTile from "../../../leaf/LeafTile/LeafTile";
import defaultContent from "../../../../content/volumeRequirementTile";
import { useLeafVolumeLabel } from "../../../leaf/LeafVolumeLabel/LeafVolumeLabel";
import { LeafMessageBox, LeafMessageBoxColor } from "../../../leaf/LeafMessageBox/LeafMessageBox";
import "./VolumeRequirementTile.scss";
import { Button } from "../../Button/Button";
import LeafTooltip from "../../../leaf/LeafTooltip/LeafTooltip";
import { IconInfoCircle } from "../../../icons/IconInfoCircle/IconInfoCircle";
import useWindowSize from "../../../hooks/useWindowSize";
import breakpoints from "../../../scss/breakpoints.module.scss";

type ButtonData = {
  clickFunction: Function;
  text: string;
  ref: RefObject<HTMLButtonElement | null>;
  icon?: JSX.Element;
};

type Props = {
  title: string;
  progressLabel?: string;
  volumeNeededLabel?: ReactNode;
  isLoading: boolean;
  currentAmount: number;
  targetAmount: number;
  met: boolean;
  circleCheckmarkId: string;
  volumeType: string;
  additionalNeeded?: number | string;
  labels?: string[];
  showLabelsOnly?: boolean;
  exempt?: boolean;
  exemptTooltipContent?: string;
  buttonData?: ButtonData;
  definition?: string;
  hideBorder?: boolean;
  isMq?: boolean;
  subText?: string;
  disableTile?: boolean;
  lsExemptMode?: boolean;
  lsExemptTagContent?: string;
  titleTooltipContent?: string;
};

export const VolumeRequirementTile = ({
  isLoading,
  currentAmount,
  targetAmount,
  circleCheckmarkId,
  met,
  title,
  progressLabel,
  volumeNeededLabel,
  volumeType,
  additionalNeeded,
  labels,
  showLabelsOnly,
  exempt,
  exemptTooltipContent,
  buttonData,
  definition = "",
  hideBorder = false,
  isMq = false,
  subText,
  disableTile = false,
  lsExemptMode = false,
  lsExemptTagContent,
  titleTooltipContent,
}: Props) => {
  const { t } = useTranslation(["volumeRequirementTile"]);
  const { user } = useAppSelector((state) => state.boot);
  const locale = storedLocale();
  const getVolumeLabel = useLeafVolumeLabel();
  const { width } = useWindowSize();
  const isMobile = Number(width) <= Number(breakpoints.tablet);

  const subtitle = progressLabel
    ? progressLabel
    : t("progressLabel", defaultContent.progressLabel, {
        targetAmount: formatReqVolume(targetAmount, locale, user.isoCountryCode),
      });

  const volumeTypeLabel = getVolumeLabel(volumeType);

  // Generate default labels for LeafProgressCircle if not provided
  // This ensures consistent decimal formatting in hover tooltips
  // Round to 2 decimal places to avoid floating-point precision issues
  const round2 = (value: number) => Math.round(value * 100) / 100;
  const remaining = Math.max(targetAmount - currentAmount, 0);
  const defaultLabels = [
    formatAboBV(currentAmount, locale, user.isoCountryCode),
    formatAboBV(round2(remaining), locale, user.isoCountryCode),
  ];
  const circleLabels = labels || defaultLabels;

  const mainClassName = `LeafCircleProgressTile ${disableTile ? "disabledMode" : ""} ${
    lsExemptMode ? "LeafCircleProgressTile--ls-exempt" : ""
  }`;

  const titleWithTooltip = lsExemptTagContent ? (
    <span className="volume-req-title--exempt">{title}</span>
  ) : exempt ? (
    <span className="volume-req-title-with-tooltip">
      <span className={exempt ? "volume-req-title--exempt" : ""}>{title}</span>
      {exemptTooltipContent && (
        <LeafTooltip
          id="volumeRequirementTileExemptTooltip"
          icon={<IconInfoCircle />}
          content={exemptTooltipContent}
          ariaLabel="Exempt requirement information"
          openOnClick={true}
          backgroundColor="blue"
          position={isMobile ? "bottom" : "right"}
        />
      )}
    </span>
  ) : titleTooltipContent ? (
    <span className="volume-req-title-with-tooltip">
      <span>{title}</span>
      <LeafTooltip
        id={`volumeRequirementTileTitleTooltip-${circleCheckmarkId}`}
        icon={<IconInfoCircle />}
        content={titleTooltipContent}
        ariaLabel="Requirement information"
        openOnClick={true}
        backgroundColor="blue"
        position={isMobile ? "bottom" : "right"}
      />
    </span>
  ) : (
    title
  );

  return (
    <div className={mainClassName}>
      <Trans>
        <LeafTile
          showMetStatus={true}
          circleCheckmarkId={circleCheckmarkId}
          met={met}
          title={titleWithTooltip}
          isLoading={isLoading}
          hideBorder={hideBorder}
        >
          <div className="VolumeReqCircle">
            {lsExemptTagContent && (
              <div className="VolumeReqCircle__exemptTag">
                <LeafMessageBox color={LeafMessageBoxColor.BLUE}>{lsExemptTagContent}</LeafMessageBox>
              </div>
            )}
            {exempt && !lsExemptMode ? (
              <LeafProgressCircle
                currentAmount={targetAmount}
                targetAmount={targetAmount}
                subtitle={subtitle}
                isLoading={isLoading}
                animationDuration={0}
                labels={circleLabels}
                showLabelsOnly={showLabelsOnly}
                ariaTitle={title}
                met={exempt}
              />
            ) : (
              <LeafProgressCircle
                currentAmount={currentAmount}
                targetAmount={targetAmount}
                subtitle={subtitle}
                isLoading={isLoading}
                animationDuration={0}
                labels={circleLabels}
                showLabelsOnly={showLabelsOnly}
                ariaTitle={title}
                met={met}
              />
            )}

            {!subText && <div className="VolumeReqCircle__definition">{definition}</div>}
            {buttonData ? (
              <div className="LeafCircleProgressTile__button-container">
                <Button
                  className="LeafCircleProgressTile__button"
                  onClick={() => buttonData.clickFunction()}
                  ref={buttonData.ref}
                >
                  <span className="LeafCircleProgressTile__button-text">{buttonData.text}</span>
                  <span className="LeafCircleProgressTile__button-icon">{buttonData.icon}</span>
                </Button>
              </div>
            ) : null}
            <span className="LeafCircleProgressTile__tag">
              {subText && <div className="LeafCircleProgressTile__tag__subtext">{subText}</div>}
              <LeafTag hideTag={exempt || met || targetAmount - currentAmount <= 0} variant="orange">
                {volumeNeededLabel || (
                  <Trans
                    i18nKey="additionalNeeded"
                    defaults={defaultContent.additionalNeeded}
                    ns="volumeRequirementTile"
                    values={{
                      volumeNeeded:
                        additionalNeeded ?? formatReqVolume(targetAmount - currentAmount, locale, user.isoCountryCode),
                      volumeType: volumeTypeLabel,
                    }}
                    components={{ 1: <strong /> }}
                  />
                )}
              </LeafTag>
            </span>
            {exempt && !disableTile && !lsExemptMode ? (
              <p className="VolumeReqCircle__exempt">
                {user.isoCountryCode === "KR"
                  ? t("lsExemptLabel", defaultContent.lsExemptLabel)
                  : t("exemptLabel", defaultContent.exemptLabel, { volumeType: volumeTypeLabel })}
              </p>
            ) : null}
            {isMq ? <p className="VolumeReqCircle__mq">{t("isMQ", defaultContent.isMQ)}</p> : null}
          </div>
        </LeafTile>
      </Trans>
    </div>
  );
};
